import html2pdf from 'html2pdf.js'
import i18n from '../i18n'

const A4_WIDTH = 794 // ~210mm at 96dpi
const A4_PAGE_HEIGHT_PX = Math.floor((A4_WIDTH * 297) / 210)
const PAGE_PAD_TOP = 30
const PAGE_PAD_BOTTOM = 32
const FOOTER_PAD_BOTTOM = 18
const LABEL_KEEP_WITH_NEXT = 140
const CAPTURE_SCALE = 2
const MAX_CANVAS_PX = 16000

const KEEP_TOGETHER = [
  '.avoid-break',
  '.pdf-avoid-break',
  '.ma-pdf-section-label',
  '.ma-pdf-kpi-grid',
  '.ma-pdf-summary',
  '.ma-pdf-card',
  '.ma-pdf-hbar',
  '.ma-pdf-footer',
  '.ap-pdf-row',
  '.ap-pdf-trow',
].join(',')

/** Height a section label needs so it lands on the same page as its first block. */
function labelKeepHeight(label: HTMLElement, pageHeight: number) {
  const labelRect = label.getBoundingClientRect()
  const next = label.nextElementSibling as HTMLElement | null
  if (!next) return labelRect.height
  const first =
    (next.matches('.ap-pdf-grid')
      ? (next.firstElementChild as HTMLElement | null)
      : next) || next
  const span = first.getBoundingClientRect().bottom - labelRect.top
  const usable = pageHeight - PAGE_PAD_TOP - PAGE_PAD_BOTTOM
  return span <= usable ? span : labelRect.height + LABEL_KEEP_WITH_NEXT
}

/**
 * Push blocks that would cross an A4 boundary onto the next page by adding
 * margin-top. Blocks taller than a page are left to their own children.
 */
function paginate(root: HTMLElement, pageHeight: number) {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>(KEEP_TOGETHER))
  blocks.forEach((block) => {
    const rootTop = root.getBoundingClientRect().top
    const rect = block.getBoundingClientRect()
    const top = rect.top - rootTop
    let bottom = rect.bottom - rootTop
    if (block.matches('.ma-pdf-section-label')) {
      bottom = top + labelKeepHeight(block, pageHeight)
    }
    const height = bottom - top
    if (height <= 0 || height > pageHeight - PAGE_PAD_TOP - PAGE_PAD_BOTTOM) {
      return
    }

    const page = Math.floor(top / pageHeight)
    const pageStart = page * pageHeight
    const pageEnd = pageStart + pageHeight
    const padBottom = block.matches('.ma-pdf-footer')
      ? FOOTER_PAD_BOTTOM
      : PAGE_PAD_BOTTOM
    let target: number | null = null
    if (bottom > pageEnd - padBottom) {
      target = pageEnd + PAGE_PAD_TOP
    } else if (page > 0 && top < pageStart + PAGE_PAD_TOP) {
      target = pageStart + PAGE_PAD_TOP
    }
    if (target === null) return

    const margin = parseFloat(getComputedStyle(block).marginTop) || 0
    block.style.marginTop = `${margin + (target - top)}px`
  })
}

/**
 * Export a DOM node to a colored PDF via html2canvas.
 * Avoids browser print (which drops backgrounds) and avoids capturing
 * off-screen / transformed nodes (which produces blank pages).
 *
 * Also temporarily clears `html { zoom: 0.8 }` — html2canvas mis-measures
 * glyphs under CSS zoom (overlapping characters / missing spaces).
 */
export async function exportElementAsColoredPdf({
  element,
  filename,
}: {
  element: HTMLElement
  filename: string
}): Promise<void> {
  const A4_WIDTH_PX = A4_WIDTH
  const htmlEl = document.documentElement
  const prevHtmlZoom = htmlEl.style.getPropertyValue('zoom')
  const captureRoot = document.createElement('div')
  const cover = document.createElement('div')

  captureRoot.setAttribute('data-ma-pdf-capture', 'true')
  captureRoot.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${A4_WIDTH_PX}px`,
    'background:#ffffff',
    'z-index:2147483645',
    'opacity:1',
    'visibility:visible',
    'pointer-events:none',
    'overflow:visible',
    'zoom:1',
  ].join(';')

  cover.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483646',
    'background:#f1f4fa',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'font-family:Lato,Helvetica,Arial,sans-serif',
    'font-size:15px',
    'font-weight:600',
    'color:#00146d',
    'pointer-events:none',
    'zoom:1',
  ].join(';')
  cover.textContent = 'Generating PDF…'

  const clone = element.cloneNode(true) as HTMLElement

  // cloneNode copies <canvas> elements without their pixels (chart.js charts)
  const sourceCanvases = Array.from(element.querySelectorAll('canvas'))
  const clonedCanvases = Array.from(clone.querySelectorAll('canvas'))
  clonedCanvases.forEach((copy, i) => {
    const source = sourceCanvases[i]
    if (!source || !source.width || !source.height) return
    try {
      const img = document.createElement('img')
      img.src = source.toDataURL('image/png')
      const rect = source.getBoundingClientRect()
      const w = source.style.width || `${rect.width || source.width}px`
      const h = source.style.height || `${rect.height || source.height}px`
      img.style.cssText = `display:block;width:${w};height:${h};max-width:100%;margin:0 auto`
      copy.replaceWith(img)
    } catch {
      // tainted canvas — leave the blank copy
    }
  })

  clone.style.cssText = [
    'display:block',
    `width:${A4_WIDTH_PX}px`,
    'max-width:100%',
    'margin:0',
    'transform:none',
    'position:relative',
    'left:0',
    'top:0',
    'opacity:1',
    'visibility:visible',
    'background:#ffffff',
    '-webkit-print-color-adjust:exact',
    'print-color-adjust:exact',
    'zoom:1',
    'letter-spacing:0',
    'word-spacing:normal',
  ].join(';')

  // Neutralize global html zoom for accurate canvas text metrics
  htmlEl.style.setProperty('zoom', '1')

  captureRoot.appendChild(clone)
  document.body.appendChild(captureRoot)
  document.body.appendChild(cover)

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready
    }
    await Promise.all(
      Array.from(clone.querySelectorAll('img')).map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((r) => {
              img.onload = r
              img.onerror = r
            })
      )
    )
    // Layout + SVG paint
    await new Promise((r) => requestAnimationFrame(() => r(undefined)))
    await new Promise((r) => setTimeout(r, 450))

    paginate(clone, A4_PAGE_HEIGHT_PX)
    const cloneTop = clone.getBoundingClientRect().top
    const lastChild = clone.lastElementChild as HTMLElement | null
    const contentHeight = lastChild
      ? Math.max(1, lastChild.getBoundingClientRect().bottom - cloneTop)
      : Math.max(clone.scrollHeight, clone.offsetHeight, 1)
    const pageCount = Math.max(1, Math.ceil(contentHeight / A4_PAGE_HEIGHT_PX))
    clone.style.height = `${pageCount * A4_PAGE_HEIGHT_PX}px`
    const canvasHeight = pageCount * A4_PAGE_HEIGHT_PX

    const options = {
      margin: 0,
      filename: filename.endsWith('.pdf') ? filename : `${filename}.pdf`,
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: {
        scale: CAPTURE_SCALE,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: A4_WIDTH_PX,
        windowWidth: A4_WIDTH_PX,
        height: canvasHeight,
        windowHeight: canvasHeight,
        x: 0,
        y: 0,
        onclone: (clonedDoc: Document) => {
          clonedDoc.documentElement.style.setProperty(
            '-webkit-print-color-adjust',
            'exact'
          )
          clonedDoc.documentElement.style.setProperty(
            'print-color-adjust',
            'exact'
          )
          clonedDoc.documentElement.style.setProperty('zoom', '1')
          clonedDoc.body.style.background = '#ffffff'
          clonedDoc.body.style.margin = '0'
          clonedDoc.body.style.setProperty('zoom', '1')

          const root = clonedDoc.querySelector(
            '[data-ma-pdf-capture]'
          ) as HTMLElement | null
          if (root) {
            root.style.transform = 'none'
            root.style.left = '0'
            root.style.top = '0'
            root.style.opacity = '1'
            root.style.visibility = 'visible'
            root.style.position = 'static'
            root.style.setProperty('zoom', '1')
          }

          clonedDoc.querySelectorAll('.ma-pdf').forEach((node) => {
            const el = node as HTMLElement
            el.style.width = `${A4_WIDTH_PX}px`
            el.style.maxWidth = '100%'
            el.style.transform = 'none'
            el.style.setProperty('zoom', '1')
            el.style.setProperty('-webkit-print-color-adjust', 'exact')
            el.style.setProperty('print-color-adjust', 'exact')
          })

          // html2canvas breaks letter-spacing (overlapping glyphs / missing spaces)
          clonedDoc.querySelectorAll('.ma-pdf, .ma-pdf *').forEach((node) => {
            const el = node as HTMLElement
            el.style.letterSpacing = '0'
            el.style.wordSpacing = 'normal'
            el.style.textRendering = 'geometricPrecision'
          })
        },
      },
      jsPDF: {
        unit: 'mm' as const,
        format: 'a4' as const,
        orientation: 'portrait' as const,
      },
      // Breaks are placed by paginate(); html2pdf's own spacers would grow the
      // content past the measured canvas height and crop the last pages.
      pagebreak: { mode: ['legacy'] as const },
    }

    // Browsers cap canvas height (~32k px), so tall reports are captured in
    // slices of a few pages and stitched into one jsPDF document.
    const chunkPages = Math.max(
      1,
      Math.floor(MAX_CANVAS_PX / (A4_PAGE_HEIGHT_PX * CAPTURE_SCALE))
    )
    const chunkOptions = (startPage: number, pages: number) => ({
      ...options,
      html2canvas: {
        ...options.html2canvas,
        y: startPage * A4_PAGE_HEIGHT_PX,
        height: pages * A4_PAGE_HEIGHT_PX,
      },
    })

    const firstPages = Math.min(chunkPages, pageCount)
    const pdf = await html2pdf()
      .set(chunkOptions(0, firstPages))
      .from(clone)
      .toPdf()
      .get('pdf')

    for (let start = firstPages; start < pageCount; start += chunkPages) {
      const pages = Math.min(chunkPages, pageCount - start)
      const canvas: HTMLCanvasElement = await html2pdf()
        .set(chunkOptions(start, pages))
        .from(clone)
        .toCanvas()
        .get('canvas')
      const pagePx = Math.round(canvas.height / pages)
      for (let p = 0; p < pages; p++) {
        const slice = document.createElement('canvas')
        slice.width = canvas.width
        slice.height = pagePx
        const ctx = slice.getContext('2d')
        if (!ctx) continue
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, slice.width, slice.height)
        ctx.drawImage(canvas, 0, -p * pagePx)
        pdf.addPage()
        pdf.addImage(
          slice.toDataURL('image/jpeg', 0.98),
          'JPEG',
          0,
          0,
          210,
          297
        )
      }
    }

    const totalPages = pdf.getNumberOfPages()
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(7.5)
    pdf.setTextColor(138, 155, 167)
    for (let page = 1; page <= totalPages; page++) {
      pdf.setPage(page)
      pdf.text(
        i18n.t('analysis.pdf.pageNumber', { page, total: totalPages }),
        105,
        295,
        { align: 'center' }
      )
    }

    pdf.save(options.filename)
  } finally {
    cover.remove()
    captureRoot.remove()
    if (prevHtmlZoom) {
      htmlEl.style.setProperty('zoom', prevHtmlZoom)
    } else {
      htmlEl.style.removeProperty('zoom')
    }
  }
}
